import {useCallback, useEffect, useMemo, useRef} from 'react';
import type {IProduct} from '../../../../../../types/galleryTypes';
import {getAllVisibleOptions} from '../getVisibleOptions';
import {useGlobals} from '../../../../../../globalPropsContext';
import {AddToCartActionStatus} from '../../../../../../constants';

type IProductOption = IProduct['options'][0];

interface UseOptionFocusManagementParams {
  product: IProduct;
  isOptionSelected: (option: IProductOption) => boolean;
}

export const useOptionFocusManagement = ({product, isOptionSelected}: UseOptionFocusManagementParams) => {
  const {addedToCartStatus} = useGlobals();
  const optionMappedRefs = useRef<Map<string, HTMLElement>>(new Map());

  const hasAddToCartError = useMemo(
    () => addedToCartStatus[product.id] === AddToCartActionStatus.UNSELECTED_OPTION_ERROR,
    [addedToCartStatus, product.id]
  );
  const hasAddToCartErrorRefValue = useRef<boolean>(hasAddToCartError);

  const handleFocusOnFirstUnselectedOption = useCallback(() => {
    const visibleOptions = getAllVisibleOptions(product.options);
    const firstUnselectedOption = visibleOptions.find((option) => !isOptionSelected(option));
    const firstUnselectedOptionElement = optionMappedRefs.current.get(firstUnselectedOption?.id);
    const firstUnselectedOptionFocusableElement =
      firstUnselectedOptionElement?.querySelector<HTMLElement>('input, button, select');

    firstUnselectedOptionFocusableElement?.focus();
  }, [product.options, isOptionSelected]);

  useEffect(() => {
    const prevHasAddToCartErrorValue = hasAddToCartErrorRefValue.current;
    const hasAddToCartErrorValueChanged = prevHasAddToCartErrorValue !== hasAddToCartError && hasAddToCartError;

    if (hasAddToCartErrorValueChanged) {
      handleFocusOnFirstUnselectedOption();
      hasAddToCartErrorRefValue.current = hasAddToCartError;
    }
  }, [hasAddToCartError, handleFocusOnFirstUnselectedOption]);

  const registerOptionRef = useCallback((optionId: string, element: HTMLElement) => {
    if (element) {
      optionMappedRefs.current.set(optionId, element);
    } else {
      optionMappedRefs.current.delete(optionId);
    }
  }, []);

  return {registerOptionRef};
};
